<template>
    <div>
        <common-notification
            ref="commonSuccessNotificationChild"
            :content="$t('pages.referrals.show.details.request_sent')"
        ></common-notification>

        <div class="ref-heading-line">
            <div class="ref-status-wrapper">
                <referrals-status :referral="latestReferral" :isInline="true"></referrals-status>
            </div>
            <div v-if="!isJustCancelled" class="ref-context-menu-wrapper">
                <referral-details-context-menu
                    :referral="latestReferral"
                    :isDisabled="false"
                    @reschedule="openRescheduleModal"
                    @follow-up-visit="openFollowUpVisitModal"
                    @cancel="openCancellationModal"
                    @match="matchReferral"
                    @patientSeen="openPatientSeenModal"
                    @addReport="addNotesClicked"
                    @downloadPdf="downloadPdf"
                    @requestReport="openRequestReportModal"
                ></referral-details-context-menu>
            </div>
        </div>

        <common-content-wrapper v-if="!isJustCancelled && showReferralDetails" width="medium">
            <common-content-spacer height="large">
                <referral-details class="referral-view-details__container" :referral="referral"></referral-details>

                <common-buttons>
                    <common-button
                        :label="$t('pages.referrals.show.view_details.buttons.back')"
                        type="primary"
                        @click="closeReferralDetails"
                    ></common-button>
                </common-buttons>
            </common-content-spacer>
        </common-content-wrapper>

        <div v-show="!isJustCancelled && !showReferralDetails" class="ref-show-details-main-container">
            <div>
                <h3>
                    {{ $t('pages.referrals.show.details.patient_name') }}:
                    <span :class="referral.shouldShowPatientInfo ? 'text-blue' : 'text-blured'" @click="openReferralInfoModal()">
                        {{ patientFullName }}
                    </span>
                </h3>
                <p>
                    {{ $t('pages.referrals.show.details.diagnosis') }}: {{ referral.diagnosis }} |
                    {{ $t('pages.referrals.show.details.service_required') }}:
                    {{ $t('pages.referrals.show.details.physician_visit') }}
                </p>
                <p v-if="shouldShowFacilityTests">{{ $t('pages.referrals.show.details.tests') }}: {{ formattedTests() }}</p>
            </div>

            <div class="referrals_confirmed_main">
                <div ref="verticalLine" class="vertical-line-through"></div>
                <ul>
                    <li>
                        <!-- step 1 -->
                        <div class="flexList">
                            <div class="flexListItem">
                                <div ref="step1Complete">
                                    <img class="step-complete-icon" :src="successIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ $t('pages.referrals.show.details.referral_received') }}</h4>
                                    <p>{{ createdAt }}</p>
                                </div>
                            </div>
                            <div class="flexListItem__right">
                                <p>{{ $t('pages.referrals.index.headings.from') }}</p>
                                <h4>
                                    {{ fullDoctorName }}
                                </h4>
                            </div>
                        </div>
                        <hr />
                    </li>

                    <li>
                        <!-- step 2 -->
                        <div class="flexList">
                            <div v-if="isApproved" class="flexListItem">
                                <div ref="step2Complete">
                                    <img class="step-complete-icon" :src="successIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ $t('pages.referrals.tracking.status_description.CONFIRMED') }}</h4>
                                    <p>{{ approvedAt }}</p>
                                </div>
                            </div>
                            <div v-else class="flexListItem">
                                <div ref="step2Error">
                                    <img class="step-complete-icon" :src="errorIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ notApprovedText }}</h4>
                                </div>
                            </div>
                            <common-content-spacer height="micro" class="flexListItem__right">
                                <div>
                                    <p>{{ $t('pages.referrals.index.headings.scheduled_for') }}</p>
                                    <p class="datetime">
                                        <b>{{ scheduledForDate }}</b>
                                        <span>{{ $t('pages.referrals.show.view_details.at') }}</span>
                                        <b>{{ scheduledForTime }}</b>
                                    </p>
                                </div>
                                <div v-if="shouldShowReferralDetails(referral) && !$auth.isUserPracticeOther()">
                                    <a @click="openReferralDetails">
                                        {{ $t('pages.referrals.show.view_details.buttons.view_details') }}
                                    </a>
                                </div>
                            </common-content-spacer>
                        </div>
                        <hr />
                    </li>

                    <li v-if="!hideInsuranceStep">
                        <!-- step 3 -->
                        <template v-if="!isApproved">
                            <div class="flexList">
                                <div class="flexListItem">
                                    <div ref="step3Pending">
                                        <img class="step-complete-icon" :src="pendingIcon" />
                                    </div>
                                    <div class="flexListItem__child">
                                        <h4>{{ $t('pages.referrals.show.details.insurance_authorization_status') }}</h4>
                                    </div>
                                </div>
                                <div class="flexListItem__right">
                                    <p>{{ $t('pages.referrals.index.headings.from') }}</p>
                                    <h4>{{ latestReferral.insurancePlan.name }}</h4>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div v-if="isInsuranceAuthObtained" class="flexList">
                                <div class="flexListItem">
                                    <div ref="step3Complete">
                                        <img class="step-complete-icon" :src="successIcon" />
                                    </div>
                                    <div class="flexListItem__child">
                                        <h4>{{ $t('pages.referrals.show.details.insurance_authorization_obtained') }}</h4>
                                        <p>{{ insuranceAuthObtainedAt }}</p>
                                    </div>
                                </div>
                                <div class="flexListItem__right">
                                    <p>{{ referral.insurancePlan.name }}</p>
                                    <h4 v-if="referral.insuranceAuthNumber">#{{ referral.insuranceAuthNumber }}</h4>
                                </div>
                            </div>

                            <div v-else class="flexList">
                                <div v-if="isInsuranceAuthDeclined" class="flexListItem">
                                    <div ref="step3Error">
                                        <img class="step-complete-icon" :src="errorIcon" />
                                    </div>
                                    <div class="flexListItem__child">
                                        <h4>{{ $t('pages.referrals.show.details.insurance_authorization_declined') }}</h4>
                                        <p>
                                            {{ $t('pages.referrals.show.details.insurance_plan') }}:
                                            {{ referral.insurancePlan.name }}
                                        </p>
                                    </div>
                                </div>
                                <div v-else class="flexListItem">
                                    <div ref="step3Pending">
                                        <img class="step-complete-icon" :src="pendingIcon" />
                                    </div>
                                    <div class="flexListItem__child">
                                        <h4>{{ $t('pages.referrals.show.details.insurance_authorization_pending') }}</h4>
                                        <p>
                                            {{ $t('pages.referrals.show.details.insurance_plan') }}:
                                            {{ referral.insurancePlan.name }}
                                        </p>
                                    </div>
                                </div>
                                <div class="flexListItem__right">
                                    <common-button
                                        v-if="referral.state !== 'CLOSED' && !isAppointmentInPast(referral)"
                                        type="primary"
                                        :label="$t('pages.referrals.show.buttons.update_status')"
                                        @click="openInsuranceAuthorizationModal"
                                    ></common-button>
                                    <span v-else>&nbsp;</span>
                                </div>
                            </div>
                        </template>
                        <hr />
                    </li>

                    <li>
                        <!-- step 4 -->
                        <div class="flexList">
                            <div v-if="isReferralTerminatedAfterApproval" class="flexListItem">
                                <div ref="step4Error">
                                    <img class="step-complete-icon" :src="errorIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ cancelOrNoShowText }}</h4>
                                    <p>{{ scheduledForDate }}</p>
                                </div>
                            </div>
                            <div
                                v-else-if="
                                    latestReferral.status === 'CONSULTATION_NOTES_PENDING' || isReferralCompleted(referral)
                                "
                                class="flexListItem"
                            >
                                <div ref="step4Complete">
                                    <img class="step-complete-icon" :src="successIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ $t('pages.referrals.show.details.patient_seen') }}</h4>
                                    <p>{{ scheduledForDate }}</p>
                                </div>
                            </div>
                            <div v-else class="flexListItem">
                                <div ref="step4Pending">
                                    <img class="step-complete-icon" :src="pendingIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ $t('pages.referrals.show.details.patient_visit') }}</h4>
                                    <p>
                                        {{ scheduledForDate }}
                                        <span v-if="isAppointmentInPast(latestReferral) && !isReferringPhysician">
                                            &nbsp;{{ $t('pages.referrals.show.details.at') }} {{ scheduledForTime }}
                                        </span>
                                    </p>
                                </div>
                            </div>
                            <div class="flexListItem__right">
                                <template v-if="showUpdateStatus">
                                    <common-button
                                        type="primary"
                                        size="large"
                                        :label="$t('pages.referrals.show.buttons.update_status')"
                                        @click="openPatientSeenModal"
                                    ></common-button>
                                </template>
                                <template v-else>
                                    <p>{{ $t('pages.referrals.show.details.at') }}</p>
                                    <h4>{{ scheduledForTime }}</h4>
                                </template>
                            </div>
                        </div>
                        <hr />
                    </li>

                    <li>
                        <!-- step 5 -->
                        <div v-if="referral.status === 'COMPLETED'" class="flexList">
                            <div class="flexListItem">
                                <div ref="step5Complete">
                                    <img class="step-complete-icon" :src="successIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ $t('pages.referrals.show.view_details.notes_uploaded') }}</h4>
                                    <p>{{ notesUploadedDate }}</p>
                                </div>
                            </div>
                            <div v-if="!$auth.isUserPracticeOther()" class="flexListItem__right">
                                <router-link
                                    :to="{ name: `referrals.${$route.params.type}.report` }"
                                    class="actions-layout__logo"
                                >
                                    <common-button
                                        :label="$t('pages.referrals.show.view_details.view_report')"
                                        type="primary"
                                        size="large"
                                    ></common-button>
                                </router-link>
                            </div>
                        </div>

                        <div
                            v-else-if="referral.status === 'CONSULTATION_NOTES_PENDING' && $route.params.type === 'received'"
                            class="flexList"
                        >
                            <div class="flexListItem">
                                <div ref="step5Pending">
                                    <img class="step-complete-icon" :src="pendingIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ $t('pages.referrals.show.view_details.consultation_report_pending') }}</h4>
                                </div>
                            </div>
                            <div class="flexListItem__right">
                                <common-button
                                    :label="$t('pages.referrals.show.view_details.buttons.add_report')"
                                    type="primary"
                                    @click="addNotesClicked"
                                ></common-button>
                            </div>
                        </div>

                        <div
                            v-else-if="referral.status === 'CONSULTATION_NOTES_PENDING' && $route.params.type === 'made'"
                            class="flexList"
                        >
                            <div class="flexListItem">
                                <div ref="step5Pending">
                                    <img class="step-complete-icon" :src="pendingIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ $t('pages.referrals.show.view_details.consultation_report_pending') }}</h4>
                                </div>
                            </div>
                            <div class="flexListItem__right referral-view-details__solicit-report">
                                <common-button
                                    v-if="!referral.notesRequestDateTime && !reportNewRequestDateTime"
                                    :label="$t('pages.referrals.show.view_details.buttons.request_report')"
                                    type="primary"
                                    @click="openRequestReportModal"
                                ></common-button>
                                <div v-else>
                                    <p>{{ $t('pages.referrals.show.details.report_requested') }}</p>
                                    <p>{{ referralReportRequestedDateTime }}</p>
                                    <a @click="openRequestReportModal">
                                        {{ $t('pages.referrals.show.details.solicit_again') }}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div v-else class="flexList">
                            <div class="flexListItem">
                                <div ref="step5Pending">
                                    <img class="step-complete-icon" :src="pendingIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ $t('pages.referrals.show.view_details.consultation_notes') }}</h4>
                                </div>
                            </div>
                            <div class="flexListItem__right">
                                <h2>{{ $t('pages.referrals.show.view_details.n_a') }}</h2>
                            </div>
                        </div>
                        <hr />
                    </li>

                    <li v-if="$route.params.type == 'received'">
                        <!-- step 6 -->
                        <div v-if="isReferralCompleted(referral) && referral.physicianReview?.ratings" class="flexList">
                            <div class="flexListItem">
                                <div ref="step6Complete">
                                    <img class="step-complete-icon" :src="successIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ 'Rating received' }}</h4>
                                    <p>{{ peerRatingDate }}</p>
                                </div>
                            </div>
                            <div class="flexListItem__right">
                                <common-rating-stars
                                    v-if="referral.fromUser"
                                    :rating="peerRating"
                                    :enableViewDetails="true"
                                    @viewDetailsClicked="viewPeerReviewDetailsClicked"
                                ></common-rating-stars>
                                <ratings-summary-modal
                                    v-if="showPeerReviewModal"
                                    :ratings="ratingsForPeerModal"
                                    :summaryText="summaryTextForPeerReviewModal"
                                    :summarySubText="summarySubTextForPeerReviewModal"
                                    :maxGrade="5"
                                    :highlightedRatingKey="'overallSatisfaction'"
                                    :numberedList="false"
                                    @close="viewPeerReviewDetailsModalClosed"
                                ></ratings-summary-modal>
                            </div>
                        </div>

                        <div v-else class="flexList">
                            <div class="flexListItem">
                                <div ref="step6Pending">
                                    <img class="step-complete-icon" :src="pendingIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ $t('pages.referrals.show.view_details.rating_not_available') }}</h4>
                                </div>
                            </div>
                            <div class="flexListItem__right">
                                <h2>{{ $t('pages.referrals.show.view_details.n_a') }}</h2>
                            </div>
                        </div>
                        <hr />
                    </li>

                    <li v-if="$route.params.type == 'made'">
                        <!-- step 6 -->
                        <div v-if="isReferralCompleted(referral) && referral.patientReview" class="flexList">
                            <div class="flexListItem">
                                <div ref="step6Complete">
                                    <img class="step-complete-icon" :src="successIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>
                                        {{ $t('pages.referrals.show.view_details.rating_for_received', { name: toFullName }) }}
                                    </h4>
                                    <p>{{ patientRatingDate }}</p>
                                </div>
                            </div>
                            <div class="flexListItem__right">
                                <common-rating-stars
                                    :rating="patientRating"
                                    :enableViewDetails="true"
                                    @viewDetailsClicked="viewPatientReviewDetailsClicked"
                                ></common-rating-stars>
                                <ratings-summary-modal
                                    v-if="showPatientReviewModal"
                                    :ratings="ratingsForPatientModal"
                                    :summaryText="summaryTextForPatientReviewModal"
                                    :maxGrade="5"
                                    :highlightedRatingKey="'overallSatisfaction'"
                                    :numberedList="false"
                                    @close="viewPatientReviewDetailsModalClosed"
                                ></ratings-summary-modal>
                            </div>
                        </div>

                        <div v-else class="flexList">
                            <div class="flexListItem">
                                <div ref="step6Pending">
                                    <img class="step-complete-icon" :src="pendingIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ $t('pages.referrals.show.view_details.rating_for', { name: getRatedName }) }}</h4>
                                </div>
                            </div>
                            <div class="flexListItem__right">
                                <h2></h2>
                            </div>
                        </div>
                        <hr />
                    </li>

                    <li v-if="$route.params.type == 'made'">
                        <!-- step 7 -->
                        <div v-if="isReferralCompleted(referral) && referral.physicianReview?.ratings" class="flexList">
                            <div class="flexListItem">
                                <div ref="step7Complete">
                                    <img class="step-complete-icon" :src="successIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>
                                        {{ $t('pages.referrals.show.view_details.my_rating_for_received', { name: toFullName }) }}
                                    </h4>
                                    <p>{{ peerRatingDate }}</p>
                                </div>
                            </div>
                            <div class="flexListItem__right">
                                <common-rating-stars
                                    :rating="peerRating"
                                    :enableViewDetails="true"
                                    @viewDetailsClicked="viewPeerReviewDetailsClicked"
                                ></common-rating-stars>
                                <ratings-summary-modal
                                    v-if="showPeerReviewModal"
                                    :ratings="ratingsForPeerModal"
                                    :summaryText="summaryTextForPhysicianReviewModal"
                                    :maxGrade="5"
                                    :highlightedRatingKey="'overallSatisfaction'"
                                    :numberedList="false"
                                    @close="viewPeerReviewDetailsModalClosed"
                                ></ratings-summary-modal>
                            </div>
                        </div>

                        <div v-else-if="isReferralCompleted(referral) && !referral.physicianReview?.ratings" class="flexList">
                            <div class="flexListItem">
                                <div ref="step7Pending">
                                    <img class="step-complete-icon" :src="pendingIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>
                                        {{ $t('pages.referrals.show.view_details.my_rating_for', { name: getRatedName }) }}
                                    </h4>
                                </div>
                            </div>
                            <div v-if="referral.patientReview" class="flexListItem__right">
                                <common-button
                                    :isDisabled="false"
                                    :label="$t('pages.referrals.show.buttons.rate_referral')"
                                    type="primary"
                                    size="large"
                                    @click="rateReferralClicked"
                                ></common-button>
                            </div>
                        </div>

                        <div v-else-if="referral.status !== 'COMPLETED'" class="flexList">
                            <div class="flexListItem">
                                <div ref="step7Pending">
                                    <img class="step-complete-icon" :src="pendingIcon" />
                                </div>
                                <div class="flexListItem__child">
                                    <h4>{{ 'My rating for ' + toFullName }}</h4>
                                </div>
                            </div>
                            <div class="flexListItem__right">
                                <h2></h2>
                            </div>
                        </div>
                        <hr />
                    </li>
                </ul>
            </div>
            <div>
                <div v-if="referral.status === 'COMPLETED' && referral.physicianReview?.ratings" class="referral-complete">
                    <div>
                        <img class="step-complete-icon" :src="successIcon" />
                        <p>{{ $t('pages.referrals.show.view_details.referral_completed') }}</p>
                    </div>
                </div>
            </div>
            <div class="back-button">
                <common-buttons>
                    <common-button
                        :label="$t('pages.referrals.show.buttons.back')"
                        type="primary"
                        size="large"
                        @click="goToReferralsIndexListing"
                    ></common-button>
                </common-buttons>
            </div>
        </div>
        <pending-declined v-if="isJustCancelled" :referral="latestReferral" type="cancelled"></pending-declined>
        <div class="modals">
            <referral-cancellation-modal
                v-if="showCancellationModal"
                :referral="referral"
                :type="isReferringPhysician ? 'referring' : 'referred'"
                :withSecondOption="!isReferringPhysician || isReferralError(referral)"
                @close="closeCancellationModal"
                @confirm="cancelAppointment"
                @deny="cancellationModalDenyAction"
            ></referral-cancellation-modal>
            <pending-reschedule-modal
                v-if="showRescheduleModal"
                :referral="referral"
                :followUpReferral="followUpReferral"
                @deny="
                    closeRescheduleModal()
                    openCancellationModal()
                "
                @reschedule="handleRescheduleAppointment"
                @close="closeRescheduleModal"
            ></pending-reschedule-modal>
            <pending-insurance-authorization-modal
                v-if="showInsuranceModal"
                :referral="referral"
                @close="closeInsuranceAuthorizationModal()"
                @close-and-show-info="
                    closeInsuranceAuthorizationModal()
                    openInsuranceAuthorizationInfoModal()
                "
                @declined="insuranceAuthDenied"
                @obtained="insuranceAuthObtained"
            ></pending-insurance-authorization-modal>
            <pending-insurance-authorization-info-modal
                v-if="showInsuranceInfoModal"
                :referral="referral"
                @close="closeInsuranceAuthorizationInfoModal"
            ></pending-insurance-authorization-info-modal>
            <referrals-is-patient-seen-modal
                v-if="showPatientSeenModal"
                :referral="referral"
                @patient-seen="patientSeen"
                @skip-all="showPatientSeenModal = false"
                @close="showPatientSeenModal = false"
            ></referrals-is-patient-seen-modal>
            <request-report-modal
                :isOpen="showRequestReportModal"
                :referral="referral"
                @close="closeRequestReportModal"
                @sent="requestReportSent"
            ></request-report-modal>
            <referrals-patient-details-modal
                v-if="showPatientDetailsModal"
                :referral="referral"
                @close="showPatientDetailsModal = false"
            ></referrals-patient-details-modal>

            <common-modal
                v-if="isPrescriptionModalOpen"
                :isOpen="isPrescriptionModalOpen"
                width="large"
                @close="closePrescriptionModal"
            >
                <referral-prescription :referral="referral"></referral-prescription>
            </common-modal>
        </div>
    </div>
</template>

<script>
    import dayjs from 'dayjs'
    import ReferralsStatus from './../../components/page-elements/ReferralsStatus'
    import RatingsSummaryModal from 'components/page-elements/ratings/RatingsSummaryModal'
    import PendingInsuranceAuthorizationModal from 'components/page-elements/referrals-show-pending/PendingInsuranceAuthorizationModal'
    import ReferralsAPI from 'api/ReferralsAPI'
    import PendingDeclined from 'components/page-elements/referrals-show-pending/PendingDeclined'
    import ReferralCancellationModal from 'components/page-elements/ReferralCancellationModal'
    import PendingRescheduleModal from 'components/page-elements/referrals-show-pending/PendingRescheduleModal'
    import ReferralRescheduleMixin from 'mixins/ReferralRescheduleMixin'
    import PendingInsuranceAuthorizationInfoModal from 'components/page-elements/referrals-show-pending/PendingInsuranceAuthorizationInfoModal'
    import ReferralsIsPatientSeenModal from 'components/page-elements/ReferralsIsPatientSeenModal'
    import RequestReportModal from 'components/page-elements/referrals/RequestConsultationReportModal'
    import ReferralDetailsContextMenu from 'components/page-elements/ReferralDetailsContextMenu'
    import ReferralDetails from 'components/page-elements/referrals/ReferralDetails'
    import ReferralPrescription from 'components/page-elements/referrals/ReferralPrescription'
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import ReferralMatchMixin from 'mixins/ReferralMatchMixin'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import ReferralCloseReasonEnum from '@/enums/ReferralCloseReasonEnum'
    import ReferralConfirmMixin from 'mixins/ReferralConfirmMixin'
    import DateTimeMixin from 'mixins/DateTimeMixin'
    import ReferralsPatientDetailsModal from 'components/page-elements/referrals/ReferralsPatientDetailsModal'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'

    export default {
        name: 'referrals-view-details',
        mixins: [ReferralRescheduleMixin, ReferralStatusMixin, ReferralMatchMixin, ReferralConfirmMixin, DateTimeMixin],

        components: {
            PendingInsuranceAuthorizationInfoModal,
            ReferralCancellationModal,
            PendingRescheduleModal,
            PendingDeclined,
            PendingInsuranceAuthorizationModal,
            ReferralsIsPatientSeenModal,
            ReferralsStatus,
            RatingsSummaryModal,
            ReferralDetailsContextMenu,
            RequestReportModal,
            ReferralDetails,
            ReferralPrescription,
            ReferralsPatientDetailsModal,
        },

        props: {
            referral: { type: Object, required: true },
        },

        data() {
            return {
                showPatientReviewModal: false,
                showPeerReviewModal: false,
                showInsuranceModal: false,
                showInsuranceInfoModal: false,
                showCancellationModal: false,
                showRescheduleModal: false,
                showFollowUpVisitModal: false,
                showPatientSeenModal: false,
                showRequestReportModal: false,
                isJustCancelled: false,
                tempReferral: null,
                reportNewRequestDateTime: null,
                showReferralDetails: false,
                showPatientDetailsModal: false,
                isChatModalOpen: false,
                isPrescriptionModalOpen: false,
                PracticeTypeEnum: PracticeTypeEnum,
            }
        },

        computed: {
            shouldShowFacilityTests() {
                return (
                    this.referral?.facilityServiceTests &&
                    this.referral.facilityServiceTests.length > 0 &&
                    this.referral?.practice?.practiceType === PracticeTypeEnum.ANCILLARY
                )
            },
            handleRescheduleAppointment() {
                return this.followUpReferral ? this.handleFollowUpVisit : this.rescheduleAppointment
            },
            latestReferral() {
                return this.tempReferral || this.referral
            },

            referralReportRequestedDateTime() {
                const localDateTime = this.getLocalDateTime(this.reportNewRequestDateTime || this.referral.notesRequestDateTime)
                return dayjs(localDateTime).format('MMMM D, YYYY')
            },

            isBothPracticesAncillary() {
                return (
                    this.referral?.practice?.practiceType === PracticeTypeEnum.ANCILLARY &&
                    this.referral?.fromPractice?.practiceType === PracticeTypeEnum.ANCILLARY
                )
            },

            isReferringPhysician() {
                return this.$route.params.type === 'made'
            },

            patientFullName() {
                return this.latestReferral.patient.firstName + ' ' + this.latestReferral.patient.lastName
            },

            fullDoctorName() {
                return this.latestReferral.fromUser?.firstName + ' ' + this.latestReferral.fromUser?.lastName
            },

            fromEmail() {
                return 'mailto:' + this.latestReferral.fromUser.email
            },

            toFullName() {
                if (this.latestReferral.practice?.id) {
                    return this.latestReferral.practice.name
                } else {
                    return `${this.latestReferral.toUser.firstName} ${this.latestReferral.toUser.lastName}`
                }
            },

            getRatedName() {
                if (this.latestReferral.practice?.practiceType === PracticeTypeEnum.ANCILLARY) {
                    return this.latestReferral.practice.name
                } else {
                    return `${this.latestReferral.toUser.designation} ${this.latestReferral.toUser.firstName} ${this.latestReferral.toUser.lastName}`
                }
            },

            successIcon() {
                return require('./../../../src/images/sucess-icon.svg').default
            },

            pendingIcon() {
                return require('./../../../src/images/pending-icon.svg').default
            },

            errorIcon() {
                return require('./../../../src/images/error-icon.svg').default
            },

            createdAt() {
                return dayjs(this.latestReferral.createdDateTime).format('MMMM D, YYYY')
            },

            insuranceAuthObtainedAt() {
                if (this.latestReferral.isInsuranceAuthorizationRequired) {
                    const d = dayjs(this.latestReferral.insuranceAuthorizationObtainedDateTime)
                    return d.isValid() ? d.format('MMMM D, YYYY') : ''
                } else {
                    return this.createdAt
                }
            },

            isApproved() {
                return dayjs(this.latestReferral.approveDateTime).isValid()
            },

            approvedAt() {
                return dayjs(this.latestReferral.approveDateTime).format('MMMM D, YYYY')
            },

            scheduledForDate() {
                return dayjs(this.latestReferral.appointmentDateTime).format('MMMM D, YYYY')
            },

            scheduledForTime() {
                return dayjs(this.latestReferral.appointmentDateTime).format('hh:mm a')
            },

            notesUploadedDate() {
                return dayjs(this.latestReferral.consultationNoteDateTime).format('MMMM D, YYYY')
            },

            patientRatingDate() {
                return dayjs(this.latestReferral.patientReview.createdDateTime).format('MMMM D, YYYY')
            },

            patientRating() {
                if (this.latestReferral.patientReview) {
                    let keys = this.setKeysForPatientReview()

                    return this.calculateRating(this.latestReferral.patientReview.ratings, keys)
                }

                return null
            },

            physicianRating() {
                if (this.latestReferral.physicianReview) {
                    let keys = this.setKeysForPhysicianRating()
                    return this.calculateRating(this.latestReferral.physicianReview.ratings, keys)
                }

                return null
            },

            ratingsForPatientModal() {
                if (this.latestReferral.patientReview) {
                    let ratingsWithOverall = this.getRatingsForPatientModal()
                    return ratingsWithOverall
                }

                return null
            },

            summaryTextForPatientReviewModal() {
                return this.patientFullName + ' rated ' + this.toFullName + ' with ' + this.patientRating + ' stars'
            },

            summaryTextForPhysicianReviewModal() {
                return (
                    this.fullDoctorName +
                    ' rated ' +
                    this.latestReferral.practice.name +
                    ' with ' +
                    this.physicianRating +
                    ' stars'
                )
            },

            peerRatingDate() {
                return dayjs(this.latestReferral.physicianReview.createdDateTime).format('MMMM D, YYYY')
            },

            peerRating() {
                if (this.latestReferral.physicianReview?.ratings) {
                    let keys = this.setKeysForPhysicianRating()
                    return this.calculateRating(this.latestReferral.physicianReview?.ratings, keys)
                }

                return null
            },

            ratingsForPeerModal() {
                if (this.latestReferral.physicianReview?.ratings) {
                    let rev = this.latestReferral.physicianReview.ratings
                    let ratings = []

                    if (this.latestReferral.practice.practiceType === PracticeTypeEnum.ANCILLARY) {
                        ratings = [
                            {
                                text: this.$t('pages.referrals.update.rate_referred.ratings.ancillary.qualityOfTest'),
                                key: 'qualityOfTest',
                                grade: Number(rev['qualityOfTest']),
                            },
                            {
                                text: this.$t('pages.referrals.update.rate_referred.ratings.ancillary.qualityOfTheReport'),
                                key: 'qualityOfTheReport',
                                grade: Number(rev['qualityOfTheReport']),
                            },
                            {
                                text: this.$t('pages.referrals.update.rate_referred.ratings.ancillary.patientExperience'),
                                key: 'patientExperience',
                                grade: Number(rev['patientExperience']),
                            },
                            {
                                text: this.$t('pages.referrals.update.rate_referred.ratings.ancillary.reportReceivedOnTime'),
                                key: 'reportReceivedOnTime',
                                grade: Number(rev['reportReceivedOnTime']),
                            },
                            {
                                text: this.$t('pages.referrals.update.rate_referred.ratings.ancillary.overallSatisfaction'),
                                key: 'overallSatisfaction',
                                grade: Number(rev['overallSatisfaction']),
                            },
                        ]
                    } else {
                        ratings = [
                            {
                                text: this.$t('pages.referrals.update.rate_referred.ratings.physician.overallSatisfaction'),
                                key: 'overallSatisfaction',
                                grade: Number(rev['overallSatisfaction']),
                            },
                            {
                                text: this.$t('pages.referrals.update.rate_referred.ratings.physician.professionalism'),
                                key: 'professionalism',
                                grade: Number(rev['professionalism']),
                            },
                            {
                                text: this.$t('pages.referrals.update.rate_referred.ratings.physician.fundOfKnowledge'),
                                key: 'fundOfKnowledge',
                                grade: Number(rev['fundOfKnowledge']),
                            },
                            {
                                text: this.$t('pages.referrals.update.rate_referred.ratings.physician.patientCare'),
                                key: 'patientCare',
                                grade: Number(rev['patientCare']),
                            },
                            {
                                text: this.$t('pages.referrals.update.rate_referred.ratings.physician.receiptOfConsultationNote'),
                                key: 'receiptOfConsultationNote',
                                grade: Number(rev['receiptOfConsultationNote']),
                            },
                        ]
                    }

                    return ratings
                }

                return null
            },

            summaryTextForPeerReviewModal() {
                //TODO: Header texts missing from InVision!
                let rating = this.peerRating

                switch (rating) {
                    case 5:
                        return 'Congratulations this was a 5 star rating'
                    case 4:
                        return 'Header text missing for rating 4'
                    case 3:
                        return 'Header text missing for rating 3'
                    case 2:
                        return 'Header text missing for rating 2'
                    case 1:
                        return 'Header text missing for rating 1'
                    default:
                        return null
                }
            },

            summarySubTextForPeerReviewModal() {
                //TODO: Sub header texts missing from InVision!
                let rating = this.peerRating

                switch (rating) {
                    case 5:
                        return 'Keep doing a great job!'
                    case 4:
                        return 'Sub text not specified for rating 4'
                    case 3:
                        return 'Sub text not specified for rating 3'
                    case 2:
                        return 'Sub text not specified for rating 2'
                    case 1:
                        return 'Sub text not specified for rating 1'
                    default:
                        return null
                }
            },

            isShowPage() {
                return this.$route.name === 'referrals.received.show'
            },

            requestedAction() {
                return this.isShowPage ? this.$route.query.action || '' : ''
            },

            isInsuranceAuthObtained() {
                let flag
                if (!this.latestReferral.isInsuranceAuthorizationRequired) {
                    flag = true
                } else if (this.latestReferral.status === ReferralStatusEnum.INSURANCE_AUTH_PENDING) {
                    flag = false
                } else {
                    flag = dayjs(this.latestReferral.insuranceAuthorizationObtainedDateTime).isValid()
                }
                return flag
            },

            isInsuranceAuthDeclined() {
                return (
                    this.latestReferral.status === ReferralStatusEnum.CANCELLED &&
                    this.latestReferral.closeReason === ReferralCloseReasonEnum.INSURANCE_AUTH_DENIED
                )
            },

            isReferralTerminatedAfterApproval() {
                return (
                    this.isApproved &&
                    (this.latestReferral.status === ReferralStatusEnum.NO_SHOW ||
                        (this.latestReferral.status === ReferralStatusEnum.CANCELLED &&
                            [
                                ReferralCloseReasonEnum.REFERRED_CANCELLED,
                                ReferralCloseReasonEnum.REFERRING_CANCELLED,
                                ReferralCloseReasonEnum.UNANSWERED,
                            ].includes(this.latestReferral.closeReason)))
                )
            },

            notApprovedText() {
                if (this.latestReferral.status === ReferralStatusEnum.DECLINED) {
                    return this.$t(`pages.referrals.tracking.status_title.DECLINED`)
                } else if (this.latestReferral.closeReason === ReferralCloseReasonEnum.UNANSWERED) {
                    return this.$t('pages.referrals.show.details.cancelled_by_system')
                } else {
                    return this.$t(`pages.referrals.tracking.status_title.CANCELLED`)
                }
            },

            cancelOrNoShowText() {
                if (this.latestReferral.status === ReferralStatusEnum.NO_SHOW) {
                    return this.$t('pages.referrals.show.details.no_show')
                }
                const reason = this.latestReferral.closeReason
                if (reason === ReferralCloseReasonEnum.UNANSWERED) {
                    return this.$t('pages.referrals.show.details.cancelled_by_system')
                }

                let name
                if (reason === ReferralCloseReasonEnum.REFERRED_CANCELLED) {
                    name = this.toFullName
                } else {
                    name = this.fullDoctorName
                }
                return this.$t('pages.referrals.show.details.appointment_cancelled', { name })
            },

            hideInsuranceStep() {
                return this.latestReferral.status === ReferralStatusEnum.NO_SHOW && !this.isInsuranceAuthObtained
            },

            showUpdateStatus() {
                return (
                    (this.latestReferral.status === 'PATIENT_VISIT_PENDING' ||
                        this.latestReferral.status === 'INSURANCE_AUTH_PENDING') &&
                    this.isAppointmentInPast(this.latestReferral) &&
                    !this.isReferringPhysician
                )
            },
        },

        methods: {
            formattedTests() {
                if (!this.referral?.facilityServiceTests) return ''
                return this.referral.facilityServiceTests.map((test) => `${test.name} - ${test.group?.name}`).join(', ')
            },
            handleFollowUpVisit() {
                const query = {
                    userId: this.latestReferral.fromUser.id,
                    practiceType: this.latestReferral.practice.practiceType,
                    searchBy: 'zipcode',
                    insurancePlanId: this.latestReferral.insurancePlan.id,
                    zip: this.latestReferral.practiceLocation.zip,
                    physicianId: this.latestReferral.toUser.id,
                    practiceId: this.latestReferral.practice.id,
                    practiceLocationId: 0,
                    patientId: this.latestReferral.patient.id,
                    referralId: this.latestReferral.id,
                    followUpVisit: true,
                }
                this.$router.push({
                    name: `referrals.new.make`,
                    query: query,
                })
            },
            goToReferralsIndexListing() {
                this.isReferringPhysician
                    ? this.$router.push({ name: `referrals.made.referrals` })
                    : this.$router.push({ name: `referrals.received.referrals` })
            },
            viewPatientReviewDetailsClicked() {
                this.showPatientReviewModal = true
            },
            viewPatientReviewDetailsModalClosed() {
                this.showPatientReviewModal = false
            },
            viewPeerReviewDetailsClicked() {
                this.showPeerReviewModal = true
            },
            viewPeerReviewDetailsModalClosed() {
                this.showPeerReviewModal = false
            },
            calculateRating(obj, keys) {
                let sum = keys.reduce((sum, key) => {
                    sum = sum + Number(obj[key])
                    return sum
                }, 0)

                return Number((sum / keys.length).toFixed(0))
            },
            rateReferralClicked() {
                this.$router.push({
                    name: `referrals.made.rate-referred`,
                    params: {
                        id: this.latestReferral.id,
                    },
                })
            },

            addNotesClicked() {
                this.$router.push({
                    name: `referrals.received.add-report`,
                    params: {
                        id: this.latestReferral.id,
                    },
                })
            },

            openCancellationModal() {
                this.showCancellationModal = true
            },

            closeCancellationModal() {
                this.showCancellationModal = false
            },

            downloadPdf() {
                this.isPrescriptionModalOpen = true
            },

            closePrescriptionModal() {
                this.isPrescriptionModalOpen = false
            },

            openReferralDetails() {
                this.showReferralDetails = true
            },

            closeReferralDetails() {
                if (this.$auth.isUserPracticeDemo()) {
                    this.emitEvent({ name: 'change-app-date' })
                }
                this.showReferralDetails = false
            },

            openReferralInfoModal() {
                this.showPatientDetailsModal = true
            },

            cancellationModalDenyAction() {
                this.closeCancellationModal()
                if (this.isReferringPhysician) {
                    this.matchReferral()
                } else {
                    this.openRescheduleModal()
                }
            },

            openInsuranceAuthorizationInfoModal() {
                this.showInsuranceInfoModal = true
            },

            closeInsuranceAuthorizationInfoModal() {
                this.showInsuranceInfoModal = false
            },

            openInsuranceAuthorizationModal() {
                this.showInsuranceModal = true
            },

            closeInsuranceAuthorizationModal() {
                this.showInsuranceModal = false
            },

            insuranceAuthDenied() {
                let that = this
                return this.showCancelInfo(
                    ReferralsAPI.updateInsuranceAuthorization(this.latestReferral.id, 'deny', this.$auth.getActiveMember().id),
                )
                    .then(() => {
                        that.closeInsuranceAuthorizationModal()
                        that.referral.status = ReferralStatusEnum.CANCELLED
                        this.emitEvent({ name: 'insurance-auth-declined' })
                    })
                    .then(() => this.updateVerticalLine())
            },

            insuranceAuthObtained() {
                let that = this
                return ReferralsAPI.updateInsuranceAuthorization(
                    this.latestReferral.id,
                    'approve',
                    this.$auth.getActiveMember().id,
                )
                    .then(() => {
                        that.closeInsuranceAuthorizationModal()
                        that.referral.insuranceAuthorizationObtainedDateTime = dayjs().toISOString()
                        that.referral.status = ReferralStatusEnum.PATIENT_VISIT_PENDING
                    })
                    .then(() => this.updateVerticalLine())
            },

            matchReferral() {
                this.goToMatchSearchPage(this.latestReferral, this.$auth.getActiveMember().id)
            },

            cancelAppointment() {
                return this.showCancelInfo(
                    ReferralsAPI.cancel(
                        this.$auth.isUserPracticeOther(),
                        this.latestReferral.id,
                        this.$auth.getActiveMember() && this.$auth.getActiveMember().id
                            ? this.$auth.getActiveMember().id
                            : this.$auth.getUser().id,
                    ),
                    true,
                )
                    .then(() => this.closeCancellationModal())
                    .then(() => this.updateVerticalLine())
            },

            showCancelInfo(promise, updateReferral) {
                if (!promise) {
                    promise = Promise.resolve()
                }
                return promise.then((res) => {
                    if (updateReferral) {
                        this.tempReferral = res
                    }
                    this.isJustCancelled = true
                })
            },

            openRescheduleModal() {
                this.showRescheduleModal = true
            },

            closeRescheduleModal() {
                this.showRescheduleModal = false
            },

            openFollowUpVisitModal() {
                this.showRescheduleModal = true
                this.followUpReferral = true
            },

            closeFollowUpVisitModal() {
                this.showFollowUpVisitModal = false
                this.followUpReferral = false
            },

            rescheduleAppointment() {
                this.reschedule(this.latestReferral)
            },

            openPatientSeenModal() {
                this.showPatientSeenModal = true
            },

            openRequestReportModal() {
                this.showRequestReportModal = true
            },

            closeRequestReportModal() {
                this.showRequestReportModal = false
            },

            requestReportSent() {
                this.reportNewRequestDateTime = dayjs().format()
                this.$refs.commonSuccessNotificationChild.showNotification()
                this.closeRequestReportModal()
            },

            patientSeen(event) {
                if (this.$auth.isUserPracticeDemo()) {
                    this.emitEvent({ name: 'change-referral-status' })
                    return
                }
                ReferralsAPI.updatePatientVisitStatus(this.referral.id, event.status)
                    .then((ev) => {
                        this.showPatientSeenModal = false
                        this.latestReferral.status = ev.status
                        this.latestReferral.insuranceAuthorizationObtainedDateTime = ev.insuranceAuthorizationObtainedDateTime
                        if (event.status === 'patient-seen') {
                            this.$router.push(`/referrals/received/${this.referral.id}/add-report`)
                        }
                    })
                    .then(() => this.updateVerticalLine())
            },

            updateVerticalLine() {
                if (this.isJustCancelled || this.showReferralDetails) return
                const firstStep = this.$refs.step1Complete
                const lastStepName = Object.keys(this.$refs)
                    .filter((ref) => ref.toLowerCase().endsWith('complete'))
                    .pop()
                const lastSuccessStep = this.$refs[lastStepName]

                const verticalLine = this.$refs.verticalLine

                const height = lastSuccessStep.getBoundingClientRect().bottom - firstStep.getBoundingClientRect().top
                verticalLine.style.height = `${height}px`
                verticalLine.style.left = `${
                    (lastSuccessStep.getBoundingClientRect().width - verticalLine.getBoundingClientRect().width) / 2
                }px`
            },

            setKeysForPatientReview() {
                if (this.latestReferral.practice.practiceType === PracticeTypeEnum.ANCILLARY) {
                    return ['facilityEnv', 'staffFriendliness', 'totalWaitTime', 'likelyRefer']
                } else {
                    return [
                        'officeEnvironment',
                        'staffFriendliness',
                        'waitTime',
                        'levelOfTrust',
                        'providerExplanation',
                        'providerListening',
                        'timeSpent',
                    ]
                }
            },

            setKeysForPhysicianRating() {
                if (this.latestReferral.practice.practiceType === PracticeTypeEnum.ANCILLARY) {
                    return [
                        'qualityOfTest',
                        'qualityOfTheReport',
                        'patientExperience',
                        'reportReceivedOnTime',
                        'overallSatisfaction',
                    ]
                } else {
                    return [
                        'professionalism',
                        'fundOfKnowledge',
                        'patientCare',
                        'receiptOfConsultationNote',
                        'overallSatisfaction',
                    ]
                }
            },

            getRatingsForPatientModal() {
                const rev = this.latestReferral.patientReview.ratings
                if (this.latestReferral.practice.practiceType === PracticeTypeEnum.ANCILLARY) {
                    return [
                        {
                            key: 'overallSatisfaction',
                            text: this.$t('pages.referrals.patient_review.ratings.overrall.text'),
                            grade: this.patientRating,
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.ancillary.facility.text'),
                            key: 'facilityEnv',
                            grade: Number(rev['facilityEnv']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.ancillary.staff.text'),
                            key: 'staffFriendliness',
                            grade: Number(rev['staffFriendliness']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.ancillary.wait_time.text'),
                            key: 'totalWaitTime',
                            grade: Number(rev['totalWaitTime']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.ancillary.refer.text'),
                            key: 'likelyRefer',
                            grade: Number(rev['likelyRefer']),
                        },
                    ]
                } else {
                    return [
                        {
                            key: 'overallSatisfaction',
                            text: this.$t('pages.referrals.patient_review.ratings.overrall.text'),
                            grade: this.patientRating,
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.office.text'),
                            key: 'officeEnvironment',
                            grade: Number(rev['officeEnvironment']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.staff.text'),
                            key: 'staffFriendliness',
                            grade: Number(rev['staffFriendliness']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.wait_time.text'),
                            key: 'waitTime',
                            grade: Number(rev['waitTime']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.trust.text'),
                            key: 'levelOfTrust',
                            grade: Number(rev['levelOfTrust']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.explanation.text'),
                            key: 'providerExplanation',
                            grade: Number(rev['providerExplanation']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.listening.text'),
                            key: 'providerListening',
                            grade: Number(rev['providerListening']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.physician.time_spent.text'),
                            key: 'timeSpent',
                            grade: Number(rev['timeSpent']),
                        },
                    ]
                }
            },
        },

        created() {
            let shouldTriggerActions = this.isShowPage && !this.isReferringPhysician
            const isClosed = this.latestReferral.state === 'CLOSED'
            if (!isClosed) {
                if (this.requestedAction === 'cancel') {
                    shouldTriggerActions = false
                    this.openCancellationModal()
                } else if (shouldTriggerActions && this.requestedAction === 'reschedule') {
                    shouldTriggerActions = false
                    this.openRescheduleModal()
                }
            } else {
                shouldTriggerActions = false
            }

            let status = this.latestReferral.status
            if (status === ReferralStatusEnum.CANCELLED) {
                shouldTriggerActions = false
                const wasApproved = dayjs(this.latestReferral.approveDateTime).isValid()
                if (!wasApproved) {
                    status = ReferralStatusEnum.DECLINED
                } else {
                    if (this.isInsuranceAuthObtained) {
                        status = ReferralStatusEnum.PATIENT_VISIT_PENDING
                    } else {
                        status = ReferralStatusEnum.INSURANCE_AUTH_PENDING
                    }
                }
            }

            switch (status) {
                case ReferralStatusEnum.PATIENT_VISIT_PENDING:
                    this.showPatientSeenModal = shouldTriggerActions && this.isAppointmentInPast(this.latestReferral)
                    break
                case ReferralStatusEnum.INSURANCE_AUTH_PENDING:
                    this.showPatientSeenModal = shouldTriggerActions && this.isAppointmentInPast(this.latestReferral)
                    this.showInsuranceModal = !isClosed && !this.isAppointmentInPast(this.latestReferral)
                    break
                default:
                    break
            }
        },

        mounted() {
            this.updateVerticalLine()
            window.addEventListener('resize', this.updateVerticalLine)
        },

        destroyed() {
            window.removeEventListener('resize', this.updateVerticalLine)
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .referral-view-details {
        &__solicit-report {
            p {
                color: #666666;
                font-family: Poppins;
                font-size: 14px !important;
                letter-spacing: 0.24px;
                line-height: 21px;
            }
        }
    }

    .ref-show-details-main-container {
        position: relative;
        margin: 5% 4%;

        h3 {
            span {
                cursor: pointer;
            }
        }

        p {
            margin: 0.5% 0 1% 0;
            font-size: 18px;
        }
    }

    .flexList {
        @include breakpoint(tablet-small) {
            flex-wrap: wrap;
        }
        display: flex;
        justify-content: space-between;
        height: 100%;
    }

    .flexListItem {
        display: flex;
        justify-content: center;
        height: 100%;
    }

    .flexListItem__child {
        @include breakpoint(smartphone-large) {
            min-width: 300px;
            max-width: 300px;
        }
        padding-left: 25px;
        align-self: center;
        line-height: 17px;
        min-width: 400px;

        p {
            margin: 0;
            font-size: 16px;
            padding: 0;
            display: flex;
        }

        h5 {
            font-weight: 900;
            letter-spacing: 0.1em;
        }
    }

    .flexListItem__right {
        @include breakpoint(tablet-small) {
            padding-left: 60px;
            margin-top: 10px;
        }
        line-height: 17px;
        min-width: 225px;

        p {
            margin: 0;
            font-size: 16px;
            padding: 0;
            display: flex;
        }

        h5 {
            font-weight: 900;
            letter-spacing: 0.1em;
        }
    }

    ul {
        list-style-type: none;

        li {
            margin-top: 5%;
        }
    }

    a {
        font-weight: 600;
        color: #13b3e3;
    }

    .referrals_confirmed_main {
        margin-top: 5%;
    }

    .datetime {
        font-size: 14px !important;

        span {
            margin: 0 3px;
        }
    }

    hr {
        width: 85%;
        margin-left: 7.5%;
        margin-top: 30px;
        height: 1px;
        color: #e0e0e0;
        background-color: #e0e0e0;
        border: none;
    }

    .helperPar {
        visibility: hidden;
    }

    .step-complete-icon {
        min-width: 35px;
        z-index: 1;
        position: relative;
    }

    h2 {
        font-weight: 400;
        margin-top: 2.5%;
    }

    .vertical-line-through {
        position: absolute;
        left: 0;
        height: 2px;
        border-left: 2px solid #00f3b2;
    }

    .back-button {
        margin-top: 50px;
    }

    .view-notes-link {
        cursor: pointer;
        font-weight: 600;
        color: #13b3e3;
    }

    .referral-complete {
        margin: 0;
        padding-top: 30px;
        text-align: center;
    }

    .referral-complete img {
        width: 50px;
        margin-bottom: 20px;
    }

    .referral-complete p {
        font-size: 26px;
        font-weight: 600;
    }

    .referral-complete > div {
        padding-top: 30px;
        display: inline-block;
    }
</style>
